import { config } from '../config';
import { HeyUtils } from '../core';

/**
 * Service for handling ticket-related operations
 */
export class TicketService {
  private heyUtils: HeyUtils;
  private documentCache: Map<string, string> = new Map();

  constructor(heyUtils: HeyUtils) {
    this.heyUtils = heyUtils;
  }

  /**
   * Get the ticket document receipt URL
   * @param userId User ID
   * @param tripId Trip ID
   * @param ticketNumber Ticket number
   * @param languageCode Language code (default: 'en')
   * @returns Promise with the document URL
   */
  public async getTicketDocumentReceipt(
    userId: string,
    tripId: string,
    ticketNumber: string,
    languageCode: string = 'en'
  ): Promise<{ documentUrl: string }> {
    try {
      // Check if we already have the document URL in cache
      const cacheKey = `${userId}_${tripId}_${ticketNumber}`;
      if (this.documentCache.has(cacheKey)) {
        console.debug('Using cached document URL');
        return { documentUrl: this.documentCache.get(cacheKey)! };
      }

      const url = `${HeyUtils.getInstance().getConfig().baseUrl}/v3/user/${userId}/trip/${tripId}/ticketDocumentReceipt?languageCode=${languageCode}&tickets=${ticketNumber}`;
      
      console.debug('Fetching document URL from API:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.heyUtils.userAccessToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get ticket document: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.debug('API response:', data);
      
      // Cache the document URL
      if (data && data.documentUrl) {
        this.documentCache.set(cacheKey, data.documentUrl);
      }
      
      return data;
    } catch (error) {
      console.error('Error getting ticket document:', error);
      throw error;
    }
  }

  /**
   * Download a ticket document - this method is kept for compatibility
   * but is no longer used directly as we open the URL in the component
   * @param documentUrl The URL of the document to download
   */
  public async downloadTicketDocument(documentUrl: string): Promise<void> {
    try {
      console.debug('Opening document URL:', documentUrl);
      window.open(documentUrl, '_blank');
    } catch (error) {
      console.error('Error downloading ticket document:', error);
      throw error;
    }
  }
}
